import type { AnalysisState } from '@aibi/contracts';
import {
  cardHeight,
  cardWidth,
  clampCardHeight,
  clampCardWidth,
  GRID_COLUMNS,
  MIN_CARD_HEIGHT,
  MIN_CARD_WIDTH,
  ROW_UNIT_REM,
} from '../../model/workspace-document';

/** Asas en los 4 lados y 4 esquinas. */
export type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export interface CardRect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

export interface AlignmentGuide {
  readonly axis: 'x' | 'y';
  /** Coordenada relativa al overlay del lienzo. */
  readonly position: number;
  readonly start: number;
  readonly end: number;
}

export interface CardSizePreview {
  /** Tamano continuo en px durante el arrastre (sin saltos de rejilla). */
  readonly widthPx: number;
  readonly heightPx: number;
  /** Desplazamiento visual al crecer hacia la izquierda/arriba. */
  readonly offsetX: number;
  readonly offsetY: number;
  /** Valores de rejilla a los que encajara al soltar. */
  readonly width: number;
  readonly height: number;
  readonly guides: readonly AlignmentGuide[];
  /** Anima hacia el snap antes de confirmar. */
  readonly settling?: boolean;
}

export interface ResizeMetrics {
  readonly columnPitch: number;
  readonly rowPitch: number;
  readonly gap: number;
  readonly minWidthPx: number;
  readonly minHeightPx: number;
}

export const RESIZE_EDGES: readonly ResizeEdge[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

export const EDGE_CURSOR: Readonly<Record<ResizeEdge, string>> = {
  n: 'cursor-ns-resize',
  s: 'cursor-ns-resize',
  e: 'cursor-ew-resize',
  w: 'cursor-ew-resize',
  ne: 'cursor-nesw-resize',
  nw: 'cursor-nwse-resize',
  se: 'cursor-nwse-resize',
  sw: 'cursor-nesw-resize',
};

export const EDGE_LABEL: Readonly<Record<ResizeEdge, string>> = {
  n: 'Redimensionar desde arriba',
  s: 'Redimensionar desde abajo',
  e: 'Redimensionar desde la derecha',
  w: 'Redimensionar desde la izquierda',
  ne: 'Redimensionar esquina superior derecha',
  nw: 'Redimensionar esquina superior izquierda',
  se: 'Redimensionar esquina inferior derecha',
  sw: 'Redimensionar esquina inferior izquierda',
};

const ALIGN_THRESHOLD_PX = 6;

const affects = (edge: ResizeEdge, side: 'n' | 's' | 'e' | 'w'): boolean => edge.includes(side);

export function measureResizeMetrics(grid: HTMLElement): ResizeMetrics {
  const styles = getComputedStyle(grid);
  const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
  const columnPitch = (grid.clientWidth - gap * (GRID_COLUMNS - 1)) / GRID_COLUMNS + gap;
  const rootFont = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const rowPitch = ROW_UNIT_REM * rootFont;
  return {
    columnPitch: Math.max(columnPitch, 1),
    rowPitch: Math.max(rowPitch, 1),
    gap,
    minWidthPx: Math.max(MIN_CARD_WIDTH * columnPitch - gap, 48),
    minHeightPx: Math.max(MIN_CARD_HEIGHT * rowPitch, 40),
  };
}

export function rectOf(element: HTMLElement): CardRect {
  const box = element.getBoundingClientRect();
  return { left: box.left, top: box.top, right: box.right, bottom: box.bottom };
}

export function collectSiblingRects(card: HTMLElement): CardRect[] {
  const grid = card.parentElement;
  if (grid === null) return [];
  return [...grid.querySelectorAll<HTMLElement>('[data-card-id]')]
    .filter((node) => node !== card)
    .map(rectOf);
}

/**
 * Preview continuo del arrastre: mueve solo los bordes del asa, encaja con vecinos
 * cercanos y reporta guias de alineacion relativas al overlay del lienzo.
 */
export function buildPreview(options: {
  readonly edge: ResizeEdge;
  readonly start: CardRect;
  readonly pointer: { readonly x: number; readonly y: number };
  readonly startPointer: { readonly x: number; readonly y: number };
  readonly metrics: ResizeMetrics;
  readonly others: readonly CardRect[];
  readonly overlayOrigin: { readonly left: number; readonly top: number };
}): CardSizePreview {
  const { edge, start, pointer, startPointer, metrics, others, overlayOrigin } = options;
  const deltaX = pointer.x - startPointer.x;
  const deltaY = pointer.y - startPointer.y;

  let left = start.left;
  let right = start.right;
  let top = start.top;
  let bottom = start.bottom;

  if (affects(edge, 'w')) left = start.left + deltaX;
  if (affects(edge, 'e')) right = start.right + deltaX;
  if (affects(edge, 'n')) top = start.top + deltaY;
  if (affects(edge, 's')) bottom = start.bottom + deltaY;

  ({ left, right, top, bottom } = enforceMinSize(
    { left, top, right, bottom },
    edge,
    metrics.minWidthPx,
    metrics.minHeightPx,
  ));
  ({ left, right, top, bottom } = snapToNeighbors(
    { left, top, right, bottom },
    edge,
    others,
    metrics.minWidthPx,
    metrics.minHeightPx,
  ));

  const widthPx = right - left;
  const heightPx = bottom - top;

  return {
    widthPx,
    heightPx,
    offsetX: left - start.left,
    offsetY: top - start.top,
    width: clampCardWidth((widthPx + metrics.gap) / metrics.columnPitch),
    height: clampCardHeight(heightPx / metrics.rowPitch),
    guides: alignmentGuides({ left, top, right, bottom }, others, overlayOrigin),
  };
}

/** Preview final: anima hacia el tamano de rejilla antes de confirmar el comando. */
export function settlingPreview(preview: CardSizePreview, metrics: ResizeMetrics): CardSizePreview {
  const widthPx = Math.max(preview.width * metrics.columnPitch - metrics.gap, metrics.minWidthPx);
  const heightPx = Math.max(preview.height * metrics.rowPitch, metrics.minHeightPx);
  return {
    widthPx,
    heightPx,
    offsetX: preview.offsetX < 0 ? preview.offsetX + (preview.widthPx - widthPx) : 0,
    offsetY: preview.offsetY < 0 ? preview.offsetY + (preview.heightPx - heightPx) : 0,
    width: preview.width,
    height: preview.height,
    guides: [],
    settling: true,
  };
}

export function gridSizeOf(card: AnalysisState): { width: number; height: number } {
  return { width: cardWidth(card), height: cardHeight(card) };
}

function enforceMinSize(
  rect: CardRect,
  edge: ResizeEdge,
  minWidthPx: number,
  minHeightPx: number,
): CardRect {
  let { left, top, right, bottom } = rect;
  if (right - left < minWidthPx) {
    if (affects(edge, 'w') && !affects(edge, 'e')) left = right - minWidthPx;
    else right = left + minWidthPx;
  }
  if (bottom - top < minHeightPx) {
    if (affects(edge, 'n') && !affects(edge, 's')) top = bottom - minHeightPx;
    else bottom = top + minHeightPx;
  }
  return { left, top, right, bottom };
}

function snapToNeighbors(
  rect: CardRect,
  edge: ResizeEdge,
  others: readonly CardRect[],
  minWidthPx: number,
  minHeightPx: number,
): CardRect {
  let { left, top, right, bottom } = rect;
  const targetsX: number[] = [];
  const targetsY: number[] = [];
  for (const other of others) {
    targetsX.push(other.left, other.right, (other.left + other.right) / 2);
    targetsY.push(other.top, other.bottom, (other.top + other.bottom) / 2);
  }

  const snap = (value: number, targets: readonly number[]): number => {
    let best = value;
    let bestDist = ALIGN_THRESHOLD_PX;
    for (const target of targets) {
      const dist = Math.abs(value - target);
      if (dist <= bestDist) {
        best = target;
        bestDist = dist;
      }
    }
    return best;
  };

  if (affects(edge, 'w')) left = snap(left, targetsX);
  if (affects(edge, 'e')) right = snap(right, targetsX);
  if (affects(edge, 'n')) top = snap(top, targetsY);
  if (affects(edge, 's')) bottom = snap(bottom, targetsY);

  return enforceMinSize({ left, top, right, bottom }, edge, minWidthPx, minHeightPx);
}

function alignmentGuides(
  rect: CardRect,
  others: readonly CardRect[],
  origin: { readonly left: number; readonly top: number },
): AlignmentGuide[] {
  const guides: AlignmentGuide[] = [];
  const selfX = [rect.left, rect.right, (rect.left + rect.right) / 2];
  const selfY = [rect.top, rect.bottom, (rect.top + rect.bottom) / 2];

  for (const other of others) {
    const otherX = [other.left, other.right, (other.left + other.right) / 2];
    const otherY = [other.top, other.bottom, (other.top + other.bottom) / 2];
    for (const x of selfX) {
      if (!otherX.some((ox) => Math.abs(x - ox) <= 0.5)) continue;
      guides.push({
        axis: 'x',
        position: x - origin.left,
        start: Math.min(rect.top, other.top) - origin.top,
        end: Math.max(rect.bottom, other.bottom) - origin.top,
      });
    }
    for (const y of selfY) {
      if (!otherY.some((oy) => Math.abs(y - oy) <= 0.5)) continue;
      guides.push({
        axis: 'y',
        position: y - origin.top,
        start: Math.min(rect.left, other.left) - origin.left,
        end: Math.max(rect.right, other.right) - origin.left,
      });
    }
  }

  const unique: AlignmentGuide[] = [];
  for (const guide of guides) {
    const exists = unique.some(
      (item) => item.axis === guide.axis && Math.abs(item.position - guide.position) < 0.5,
    );
    if (!exists) unique.push(guide);
  }
  return unique;
}
